import './Search.scss';
import { navigate } from 'gatsby';
import * as React from 'react';
import _ from 'lodash';
import * as Core from 'components/core';
import { DocPage } from 'types/docs';
import SearchResults from './SearchResults';
type Props = {
  currentAppVersionPath: string;
};
type State = {
  query: string;
  selectedResultIndex: number | undefined | null;
  inputHasFocus: boolean;
  results: Array<DocPage>;
  loadingSearchIndex: boolean;
  error: null | 'short-query' | 'no-results';
};
const SEARCH_DELAY_MS = 200;

// module-level variable, so we don't have to refetch
// if the Search component is destroyed
let searchIndex: Array<DocPage> | undefined | null = null;
export default class Search extends React.Component<Props, State> {
  unmounted: boolean = false;
  componentWillUnmount() {
    this.unmounted = true;
  }
  state = {
    query: '',
    selectedResultIndex: null,
    inputHasFocus: false,
    results: [],
    loadingSearchIndex: false,
    error: null,
  };
  updateQuery = (event: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedQuery = event.currentTarget.value.replace('\\', '');
    this.setState({
      query: sanitizedQuery,
      inputHasFocus: true,
    });
    this.updateResults();
  };
  updateResults = _.debounce(() => {
    if (this.unmounted) {
      return;
    }
    if (this.state.query === '') {
      this.setState({
        results: [],
        selectedResultIndex: null,
        error: null,
      });
      return;
    }
    if (!searchIndex && !this.state.loadingSearchIndex) {
      this.setState({
        loadingSearchIndex: true,
      });

      // This is built in gatsby-node.js onPostBuild.
      // To refresh this, restart the development server, or run `yarn build`
      fetch(`/search-index.json`)
        .then((response) => {
          return response.json();
        })
        .then((data) => {
          searchIndex = data;
          this.setState({
            loadingSearchIndex: false,
          });
          this.updateResults();
        });
    }
    if (this.state.query.length < 3) {
      // display message to enter more than 3 characters
      this.setState({
        results: [],
        selectedResultIndex: 0,
        error: 'short-query',
      });
      return;
    }
    const { currentAppVersionPath } = this.props;
    const queryParts = this.state.query.toLowerCase().split(' ');
    let results =
      searchIndex != null
        ? searchIndex.filter((result) => {
            // Reset the search filter rankings, seed with some safe values
            result.rank = 0;
            result.title = result.title || '';
            result.content = result.content || '';

            // And filter any results that don't belong to this app version.
            return result.path.startsWith(currentAppVersionPath);
          })
        : [];
    queryParts.forEach((queryPart) => {
      results.forEach((result) => {
        const title = result.title.toLowerCase();
        if (title.includes(queryPart)) {
          result.rank += 5;
        }
        if (result.content.includes(queryPart)) {
          result.rank += 1;
        }
        if (title === queryPart) {
          result.rank += 10;
        }
        if (title.startsWith(queryPart)) {
          result.rank += 5;
        }
      });
    });
    results = results.filter((r) => r.rank > 0);
    results = results.sort((a, b) => (a.rank < b.rank ? 1 : -1));
    this.setState({
      results,
      selectedResultIndex: 0,
      error: results.length === 0 ? 'no-results' : null,
    });
  }, SEARCH_DELAY_MS);

  // Unused, but cool
  fuzzyMatchTest = (page: DocPage) => {
    const regex = new RegExp(this.state.query.split('').join('[\\w-]*'), 'i');
    return regex.test(page.name);
  };
  handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { selectedResultIndex, results } = this.state;
    if (selectedResultIndex == null) {
      return;
    }
    const numResults = results.length;
    const selectedResult = results[selectedResultIndex];
    const resultBelowIndex = (selectedResultIndex + 1) % numResults;
    const resultAboveIndex =
      selectedResultIndex === 0 ? numResults - 1 : selectedResultIndex - 1;
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        if (selectedResult == null) {
          return;
        }
        navigate(selectedResult.path);
        this.handleSelectResult();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.setState({
          selectedResultIndex: resultBelowIndex,
        });
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.setState({
          selectedResultIndex: resultAboveIndex,
        });
        break;
    }
  };
  handleSelectResult = () => {
    if (this.unmounted) {
      return;
    }
    this.setState({
      query: '',
      inputHasFocus: false,
    });
  };
  handleBlur = () => {
    if (this.unmounted) {
      return;
    }
    this.setState({
      inputHasFocus: false,
    });
  };
  handleFocus = () => {
    this.setState({
      inputHasFocus: true,
    });
  };
  render() {
    const {
      query,
      results,
      inputHasFocus,
      selectedResultIndex,
      loadingSearchIndex,
      error,
    } = this.state;
    return (
      <div className="docs-Search" onMouseLeave={this.handleBlur}>
        <Core.Input
          type="text"
          placeholder="Search"
          value={query}
          onChange={this.updateQuery}
          onKeyDown={this.handleKeyDown}
          onClick={this.handleFocus}
          onFocus={this.handleFocus}
          aria-label="Search Docs"
        />
        {loadingSearchIndex && results.length > 0 && (
          <div className="docs-Search-results">
            <div className="docs-Search-results-result">
              <Core.ActivityIndicator />
            </div>
          </div>
        )}
        {!loadingSearchIndex && inputHasFocus && query.length > 0 && (
          <SearchResults
            rawQuery={query}
            results={results}
            onSelectResult={this.handleSelectResult}
            selectedResultIndex={selectedResultIndex}
            error={error}
          />
        )}
      </div>
    );
  }
}
